import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  listBdcInvoiceSequenceHistory,
  listBdcInvoiceSequences,
  resetBdcInvoiceSequence,
  setBdcInvoiceSequence,
} from "../../../services";
import { BdcBranch, BdcDocSequence, BdcDocSequenceHistory, BdcSequenceKind } from "../../../types/bdc.type";
import { readBdcError } from "../apiError";
import { rowsOf, thaiDateTime } from "../format";
import Tag from "../Tag";
import {
  formatSequenceNo,
  parseSequenceNoInput,
  SEQUENCE_KIND_LABEL,
  sequenceDigits,
  sequenceKind,
} from "./invoiceNo";

/** The INV series always show, even before the API has a row for one. */
const INVOICE_SERIES: Pick<BdcDocSequence, "prefix" | "kind">[] = [
  { prefix: "B", kind: "invoice" },
  { prefix: "TB", kind: "invoice" },
];
const KIND_ORDER: BdcSequenceKind[] = ["invoice", "credit_note", "book"];

const HISTORY_LIMIT = 10;

type Series = Pick<BdcDocSequence, "prefix" | "kind" | "digits" | "company_id"> & Partial<BdcDocSequence>;
type Pending = { action: "set"; prefix: string; nextNo: number } | { action: "reset"; prefix: string };

const isTestSeries = (prefix: string) => prefix.startsWith("T");

/** What the box of one series is called: "เลขถัดไป (จริง)" for INV; CN / book name their branch. */
const seriesLabel = (series: Series, branches: BdcBranch[]): string => {
  const kind = sequenceKind(series);
  if (kind === "invoice") return isTestSeries(series.prefix) ? "เลขถัดไป (ทดสอบ)" : "เลขถัดไป (จริง)";
  const branch = branches.find(
    (b) =>
      (series.company_id && b.company_id === series.company_id) ||
      (kind === "credit_note" && b.cn_prefix && series.prefix.replace(/^T/, "") === b.cn_prefix) ||
      (kind === "book" && b.invoice_book_code === series.prefix),
  );
  const where = branch?.branch_code ? ` · ${branch.branch_code}` : "";
  if (kind === "credit_note") return `CN ${series.prefix}${isTestSeries(series.prefix) ? " (ทดสอบ)" : ""}${where}`;
  return `เล่ม ${series.prefix}${where}`;
};

const nextText = (series: Series): string =>
  series.next_invoice_no ??
  (series.next_no !== undefined ? formatSequenceNo(series.prefix, series.next_no, sequenceDigits(series)) : "-");

const inUseText: Record<BdcSequenceKind, string> = {
  invoice: "เลขนี้เคยใช้แล้ว — ออก INV ไม่ได้จนกว่าจะ Set ใหม่",
  credit_note: "เลขนี้เคยใช้แล้ว — ออก CN ไม่ได้จนกว่าจะ Set ใหม่",
  book: "เลขเล่มนี้เคยใช้แล้ว — Set เลขใหม่",
};

/**
 * ตั้งค่า BDC › เลขเอกสาร — every running series: INV (one for BK and RY, `B` + 9 digits, test
 * `TB`), CN per branch (`C2` + 7 digits, test `TC2`) and the INV book number per branch (`VY`,
 * not zero-padded). Each shows its next number (amber when it is already used: issuing stops there
 * until someone Sets a new one); Set and Reset ask first and are logged. MASTER only.
 */
export default function SequenceTab({ canWrite, branches = [] }: Readonly<{ canWrite: boolean; branches?: BdcBranch[] }>) {
  const [sequences, setSequences] = useState<BdcDocSequence[]>([]);
  const [history, setHistory] = useState<BdcDocSequenceHistory[]>([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [prefix, setPrefix] = useState<string>("B");
  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSequences = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcInvoiceSequences();
      setSequences(rowsOf(data));
    } catch {
      setSequences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await listBdcInvoiceSequenceHistory(historyPage + 1, HISTORY_LIMIT);
      setHistory(data?.rows ?? []);
      setHistoryCount(data?.count ?? 0);
    } catch {
      setHistory([]);
      setHistoryCount(0);
    }
  }, [historyPage]);

  useEffect(() => {
    loadSequences();
  }, [loadSequences]);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  /** The API's series plus B / TB if it has no row for them yet, INV first, then CN, then book. */
  const series: Series[] = useMemo(() => {
    const list: Series[] = [...sequences];
    for (const inv of INVOICE_SERIES) {
      if (!list.some((s) => s.prefix === inv.prefix)) list.push(inv);
    }
    return list.sort(
      (a, b) =>
        KIND_ORDER.indexOf(sequenceKind(a)) - KIND_ORDER.indexOf(sequenceKind(b)) ||
        a.prefix.replace(/^T/, "").localeCompare(b.prefix.replace(/^T/, "")) ||
        Number(isTestSeries(a.prefix)) - Number(isTestSeries(b.prefix)),
    );
  }, [sequences]);
  const prefixes = series.map((s) => s.prefix);

  const current = series.find((s) => s.prefix === prefix) ?? { prefix, kind: "invoice" as const };
  const digits = sequenceDigits(current);
  const parsed = parseSequenceNoInput(text, current, prefixes);
  const typedError = text.trim() !== "" && "error" in parsed ? parsed.error : null;
  const format = (p: string, n: number) => formatSequenceNo(p, n, sequenceDigits(series.find((s) => s.prefix === p) ?? { prefix: p }));

  const run = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      if (pending.action === "set") await setBdcInvoiceSequence(pending.prefix, pending.nextNo);
      else await resetBdcInvoiceSequence(pending.prefix);
      setPending(null);
      setText("");
      await Promise.all([loadSequences(), loadHistory()]);
    } catch (e) {
      const failure = readBdcError(e, "บันทึกเลขเอกสารไม่สำเร็จ");
      setError(
        failure.code === "BDC_INVOICE_NO_ALREADY_USED"
          ? `เลข ${failure.invoiceNo ?? (pending.action === "set" ? format(pending.prefix, pending.nextNo) : "")} มีในเอกสารแล้ว — เลือกเลขอื่นที่ยังไม่เคยใช้`
          : failure.message,
      );
      setPending(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }} data-testid="bdc-sequence-tab">
      <Typography variant="body2" color="text.secondary">
        INV ชุดเดียวทั้ง BK และ RY · CN และเลขเล่ม INV แยกตามสาขา (ตั้ง prefix CN / รหัสเล่มที่แท็บ สาขา แล้วชุดเลขจะขึ้นที่นี่) ·
        ถ้าเลขถัดไปเคยออกแล้ว ระบบหยุดและแจ้งเตือน ไม่ข้ามเลขเอง · สาขาโหมดทดสอบใช้ชุด T…
        {!canWrite && " · Set / Reset ได้เฉพาะ MASTER"}
      </Typography>
      {loading && <LinearProgress />}
      {KIND_ORDER.map((kind) => {
        const group = series.filter((s) => sequenceKind(s) === kind);
        if (group.length === 0) return null;
        return (
          <Box key={kind}>
            <Typography variant="body2" fontWeight={600} mb={0.75}>
              {SEQUENCE_KIND_LABEL[kind]}
            </Typography>
            <Stack direction="row" gap={1.25} flexWrap="wrap">
              {group.map((seq) => (
                <Box
                  key={seq.prefix}
                  sx={{
                    flex: "1 1 200px",
                    maxWidth: 360,
                    bgcolor: seq.next_in_use ? "#FFF0D6" : "#F5F5F6",
                    borderRadius: "8px",
                    p: 1.25,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {seriesLabel(seq, branches)}
                  </Typography>
                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }} className="mono" data-testid={`bdc-next-${seq.prefix}`}>
                    {nextText(seq)}
                  </Typography>
                  {seq.next_in_use && <Tag tone="warn">{inUseText[kind]}</Tag>}
                  {seq.updated_at && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      แก้ล่าสุด {seq.updated_by_name ?? "-"} · {thaiDateTime(seq.updated_at)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}

      {canWrite && (
        <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
          <TextField
            select
            size="small"
            label="ชุด"
            value={prefix}
            onChange={(e) => {
              setPrefix(e.target.value);
              setText("");
            }}
            sx={{ minWidth: 150 }}
          >
            {series.map((s) => (
              <MenuItem key={s.prefix} value={s.prefix}>
                {s.prefix} ({SEQUENCE_KIND_LABEL[sequenceKind(s)].replace("เลข", "")}
                {isTestSeries(s.prefix) && sequenceKind(s) !== "book" ? " ทดสอบ" : sequenceKind(s) === "invoice" ? " จริง" : ""})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Set เลขถัดไป"
            placeholder={nextText(current) !== "-" ? nextText(current) : formatSequenceNo(prefix, 1, digits)}
            value={text}
            onChange={(e) => setText(e.target.value)}
            error={Boolean(typedError)}
            helperText={typedError ?? (("value" in parsed && formatSequenceNo(prefix, parsed.value, digits)) || " ")}
            sx={{ width: 200 }}
            slotProps={{ htmlInput: { "aria-label": "Set เลขถัดไป" } }}
          />
          <Button
            variant="contained"
            disabled={!("value" in parsed)}
            onClick={() => "value" in parsed && setPending({ action: "set", prefix, nextNo: parsed.value })}
          >
            Set
          </Button>
          <Button variant="outlined" color="error" onClick={() => setPending({ action: "reset", prefix })}>
            Reset เป็น 1
          </Button>
        </Stack>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        <Typography fontWeight={600} mb={1}>
          ประวัติ Set / Reset
        </Typography>
        <table className="bdc-t">
          <thead>
            <tr>
              <th>เวลา</th>
              <th>ชุด</th>
              <th>การทำ</th>
              <th>จาก → เป็น</th>
              <th>โดย</th>
            </tr>
          </thead>
          <tbody>
            {history.map((row, i) => (
              <tr key={`${row.created_at}-${i}`}>
                <td>{thaiDateTime(row.created_at)}</td>
                <td>{row.prefix}</td>
                <td>{row.action === "reset" ? <Tag tone="crit">Reset</Tag> : <Tag tone="info">Set</Tag>}</td>
                <td className="mono">
                  {row.from_invoice_no} → <b>{row.to_invoice_no}</b>
                </td>
                <td className="sub">{row.created_by_name ?? row.created_by}</td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr>
                <td colSpan={5} className="sub" style={{ textAlign: "center" }}>
                  ยังไม่มีการ Set / Reset
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {historyCount > HISTORY_LIMIT && (
          <TablePagination
            component="div"
            count={historyCount}
            page={historyPage}
            rowsPerPage={HISTORY_LIMIT}
            rowsPerPageOptions={[HISTORY_LIMIT]}
            onPageChange={(_e, next) => setHistoryPage(next)}
          />
        )}
      </Box>

      <Dialog open={pending !== null} onClose={saving ? undefined : () => setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{pending?.action === "reset" ? `Reset ชุด ${pending.prefix}` : `Set ${SEQUENCE_KIND_LABEL[sequenceKind(current)]} ถัดไป`}</DialogTitle>
        <DialogContent>
          {pending?.action === "set" && (
            <DialogContentText>
              เอกสารใบถัดไปของชุด {pending.prefix} จะได้เลข <b>{format(pending.prefix, pending.nextNo)}</b> แล้วนับต่อจากนั้น
              {nextText(current) !== "-" && ` (ตอนนี้ ${nextText(current)})`}
            </DialogContentText>
          )}
          {pending?.action === "reset" && (
            <Alert severity="error">
              เลขถัดไปของชุด {pending.prefix} จะกลับไปเริ่มที่ <b>{format(pending.prefix, 1)}</b> — ถ้าเลขนั้นเคยออกแล้ว
              การออกเอกสารจะหยุดจนกว่าจะ Set เลขใหม่
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={saving}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            color={pending?.action === "reset" ? "error" : "primary"}
            loading={saving}
            onClick={run}
          >
            ยืนยัน {pending?.action === "reset" ? "Reset" : "Set"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
